import { useMsal } from '@azure/msal-react';
import { Button, Checkbox, InfoLabel, MessageBar, MessageBarActions, MessageBarBody, Radio, SearchBox, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Tooltip, useFocusableGroup } from '@fluentui/react-components';
import { ArrowDownload16Regular, ArrowDownloadRegular, ChevronDown12Regular, ChevronUp12Regular, StarFilled } from '@fluentui/react-icons';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { downloadFile } from '../../../common/file/download-file';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { AI_SCRUB_OVERWRITE_WARNING } from '../../../common/scrubbing/ai-scrub-overwrite-warning';
import { areAllSelectedPassEligible, PASS_INELIGIBLE_TOOLTIP } from '../../../common/scrubbing/pass-eligibility';
import { NominationSummarySortKey } from '../../../common/sorting/nomination-summary-sort-key';
import { sortCountryNominationSummariesByKey } from '../../../common/sorting/sort-country-nomination-summary';
import { getUserNames } from '../../../common/string/get-user-names';
import { getTooltipContentByFieldName } from '../../../common/tooltip/tooltip-get-content';
import { blankNominationScrubbingStatus, CountryNominationSummary, NominationScrubbingStatus, ScrubbingStatusId } from '../../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../../models/resource-services';
import { RoutePaths } from '../../../route-paths';
import { AiScrubbingTriggerDialog } from '../../page-award/components/AiScrubbingTriggerDialog';
import { OverrideScrubbingModal } from '../../page-award/components/OverrideScrubbingModal';
import { clearPersistedBatchRunId, useAiScrubProgress } from '../../page-award/hooks/use-ai-scrub-progress';
import { BatchProgressIndicator } from '../../shared-components/ai-scrubbing/BatchProgressIndicator';
import { cancelOutcomeActions, cancelOutcomeMessage, cancelStatusCode, classifyCancelOutcome } from '../../shared-components/ai-scrubbing/cancel-outcome';
import { DisqualificationReasoningDialog } from '../../shared-components/ai-scrubbing/DisqualificationReasoningDialog';
import { ScoringReasoningDialog } from '../../shared-components/ai-scrubbing/ScoringReasoningDialog';
import { ExportNominationFieldsModal } from '../../shared-components/export-nominations-fields-modal/ExportNominationFieldsModal';
import { ExportNominationsModal, ExportType } from '../../shared-components/export-nominations-modal/ExportNominationsModal';
import { FlexBox } from '../../shared-components/flex-box';
import Loading from '../../shared-components/Loading';
import { AiScrubOverwriteConfirmModal, MultipleDisqualifyScrubbingModal, PassScrubbingModal } from '../../shared-components/scrubbing-modal';
import './CountryNominationSummaryTable.scss';
import { noJudgingEmptyStateLabel, scoringReasoningAvailable } from './scoring-empty-state';

interface CountryNominationSummaryTableProps {
  canAssignWinners: boolean;
  canTriggerAiScrub: boolean;
  defaultScrubbingRubric?: string;
  countryAwardId: number;
  countryId: number;
  programId: number;
  countryNominationSummaries: CountryNominationSummary[];
  onBatchTerminal(): Promise<void>;
  isScrubbingWindowOpen: boolean;
  isJudgingWindowOpen: boolean;
  downloadProgramCountryExportedNominationsSummaries(
    countryAwardId: number,
    programId: number,
    countryId: number,
    selectedFields: string[],
    searchText: string,
  ): Promise<void>;
  onSetSortedCountryNominationSummaries(summaries: CountryNominationSummary[]): void;
}

const CountryNominationSummaryTableImpl: React.FC<CountryNominationSummaryTableProps> = (props) => {
  const {
    instance,
  } = useMsal();

  const {
    canAssignWinners,
    canTriggerAiScrub,
    defaultScrubbingRubric,
    countryAwardId,
    countryId,
    programId,
    countryNominationSummaries,
    isScrubbingWindowOpen,
    isJudgingWindowOpen,
    downloadProgramCountryExportedNominationsSummaries,
    onSetSortedCountryNominationSummaries,
    onBatchTerminal,
  } = props;

  const {
    apiStore,
    apiEndpointCallStatesStore,
    awardStore,
    countryAwardPageUiStore,
    errorStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [showArrowOnKey, setShowArrowOnKey] = useState('');
  const [lastSortedKey, setLastSortedKey] = useState<string>('');
  const [sortedCountryNominationSummaries, setSortedCountryNominationSummaries] = useState<CountryNominationSummary[]>([]);
  const [selectedCountryNominationSummaries, setSelectedCountryNominationSummaries] = useState<CountryNominationSummary[]>([]);
  const [selectedProgramCountryNominationIds, setSelectedProgramCountryNominationIds] = useState<number[]>([]);
  const [selectedProgramCountryNominationId, setSelectedProgramCountryNominationId] = useState<number>(0);
  const [isShowDisqualifyScrubbingModal, setShowDisqualifyScrubbingModal] = useState(false);
  const [isShowPassScrubbingModal, setShowPassScrubbingModal] = useState(false);
  const [isShowExportNominationFieldsModal, setShowExportNominationFieldsModal] = useState<boolean>(false);
  const [isShowExportNominationsModal, setShowExportNominationsModal] = useState<boolean>(false);
  const [isShowExportNominationModal, setShowExportNominationModal] = useState<boolean>(false);
  const [reasoningDialogSummary, setReasoningDialogSummary] = useState<CountryNominationSummary | undefined>();
  const [scoringDialogSummary, setScoringDialogSummary] = useState<CountryNominationSummary | undefined>();
  const [overrideDialogSummary, setOverrideDialogSummary] = useState<CountryNominationSummary | undefined>();
  const [isAiScrubbingDialogOpen, setIsAiScrubbingDialogOpen] = useState<boolean>(false);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState<boolean>(false);
  const [hasJudgingQuestions, setHasJudgingQuestions] = useState<boolean | undefined>(undefined);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleToggleJudgeRows = (nominationId: number): void => {
    setExpandedRows(currentExpandedRows => currentExpandedRows.includes(nominationId)
      ? currentExpandedRows.filter(expandedNominationId => expandedNominationId !== nominationId)
      : [...currentExpandedRows, nominationId]);
  };

  const focusableGroupAttr = useFocusableGroup({
    tabBehavior: 'limited-trap-focus',
  });

  const someNominationsHaveWinnerRole = useMemo(() => sortedCountryNominationSummaries.some((nomination) => (nomination.role === 'Winner' || nomination.role === 'Finalist')), [sortedCountryNominationSummaries]);
  const someNominationsHaveAssignedJudge = useMemo(() => sortedCountryNominationSummaries.some((nomination) => (nomination.judges?.length ?? 0) > 1), [sortedCountryNominationSummaries]);

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;

  useAiScrubProgress({
    awardId: countryAwardId,
    instance,
    store: countryAwardPageUiStore,
    scope: 'country-awards',
    countryId,
    onTerminal: async () => {
      await onBatchTerminal();
      countryAwardPageUiStore.finalizeBatch();
    },
  });

  useEffect(() => {
    let cancelled = false;
    apiStore.getStaffJudgingQuestions(instance, countryAwardId)
      .then((questions) => { if (!cancelled) setHasJudgingQuestions(scoringReasoningAvailable(questions)); })
      .catch(() => { if (!cancelled) setHasJudgingQuestions(undefined); });
    return () => { cancelled = true; };
  }, [instance, countryAwardId, apiStore]);

  const refreshBatchStatus = async (batchRunId: string): Promise<void> => {
    try {
      const response = await apiStore.getCountryAiScrubBatchStatus(instance, countryAwardId, batchRunId);
      countryAwardPageUiStore.setBatchProgress(response.run);
      countryAwardPageUiStore.setQueueMetrics(response.queuePosition, response.queueDepth);
      countryAwardPageUiStore.setIsEvaluationRun(response.isEvaluation);
    } catch {
      // best-effort refresh; leave the panel as-is on failure
    }
  };

  const onCancelBatch = async (): Promise<void> => {
    const batchRunId = countryAwardPageUiStore.activeBatchRunId;
    if (!batchRunId) return;
    try {
      await apiStore.cancelCountryAiScrubBatch(instance, countryAwardId, batchRunId);
      // 204: the existing progress pipeline (queue poll / SSE + finish()) drives the UI to Finalized.
    } catch (err) {
      const outcome = classifyCancelOutcome(cancelStatusCode(err));
      for (const cancelAction of cancelOutcomeActions(outcome)) {
        if (cancelAction === 'clearPersistedKey') clearPersistedBatchRunId(countryAwardId, 'country-awards', countryId);
        else if (cancelAction === 'clearStore') countryAwardPageUiStore.clearBatchProgress();
        else if (cancelAction === 'refreshStatus') await refreshBatchStatus(batchRunId);
      }
      const message = cancelOutcomeMessage(outcome);
      if (message) errorStore.reportValidationError(message);
    }
  };

  const renderBatchCompletionSummary = (): React.JSX.Element | null => {
    const summary = countryAwardPageUiStore.lastBatchSummary;
    if (!summary) return null;
    const text = summary.isEvaluation
      ? `AI evaluation complete: ${summary.scrubbed} assessed, ${summary.unscrubbed} not assessed of ${summary.total}.`
      : `AI review complete: ${summary.scrubbed} scrubbed, ${summary.unscrubbed} unscrubbed of ${summary.total}.`;
    return (
      <MessageBar intent={summary.unscrubbed > 0 ? 'warning' : 'success'}>
        <MessageBarBody>{text}</MessageBarBody>
        <MessageBarActions>
          <Button appearance='transparent' onClick={() => countryAwardPageUiStore.clearLastBatchSummary()}>Dismiss</Button>
        </MessageBarActions>
      </MessageBar>
    );
  };

  const renderScoringEmptyState = (): React.JSX.Element | null => {
    if (!countryAwardPageUiStore.isScrubbingWindowAlreadyOpenButWinnersWindowNotStartYet || hasJudgingQuestions !== false) {
      return null;
    }
    return <Text className='country-nomination-summary-table__scoring-empty' size={200}>{noJudgingEmptyStateLabel}</Text>;
  };


  const getCountryAwardNominationSummaries = async (programId: number, countryId: number, searchText?: string) => {
    try {
      const nominationRefs = await apiStore.getCountryAwardNominationSummaries(instance, programId, countryId, searchText);
      countryAwardPageUiStore.setCountryNominationSummaries(nominationRefs);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const onAiReviewClicked = (): void => {
    // The AI review dialog has a country selector, so the batch's target country can differ from
    // this page's country; a page-scoped prior-result check can't predict it. Always confirm.
    setShowOverwriteConfirm(true);
  }

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        await getCountryAwardNominationSummaries(programId, countryId, query);
      }
      finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const onSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const onNominationCheckedChange = (countryNominationSummary: CountryNominationSummary, selected: boolean) => {
    const nominationSummaries = [...sortedCountryNominationSummaries];
    nominationSummaries[nominationSummaries.indexOf(countryNominationSummary)].selected = selected;
    setSortedCountryNominationSummaries(nominationSummaries);
    onSetSortedCountryNominationSummaries(nominationSummaries);

    const selectedNominationSummaries = selectedCountryNominationSummaries;
    if (selected) {
      selectedNominationSummaries.push(countryNominationSummary);
    }
    else {
      selectedNominationSummaries.splice(selectedNominationSummaries.indexOf(countryNominationSummary), 1);
    }
    setSelectedCountryNominationSummaries(selectedNominationSummaries);
    setSelectedProgramCountryNominationIds(selectedNominationSummaries.map((nomination) => nomination.nominationId));
  }

  const showArrow = (key: NominationSummarySortKey) => {
    if (showArrowOnKey !== key) return undefined;
    return (
      showArrowOnKey === lastSortedKey ?
        <>
          <ChevronDown12Regular />
        </> :
        <>
          <ChevronUp12Regular />
        </>
    )
  }

  const onCancelExporting = () => {
    setShowExportNominationFieldsModal(false);
  }

  const onExportNominations = (selectedFields: string[]) => {
    downloadProgramCountryExportedNominationsSummaries(countryAwardId, programId, countryId, selectedFields, searchText)
      .then(() => setShowExportNominationFieldsModal(false));
  }

  const download = async (req: ResourceTicketRequest): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, req);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const onExportNominationsZip = async (exportedType: ExportType): Promise<void> => {
    if (selectedProgramCountryNominationIds.length > 0) {
      await download({
        data: {
          routeData: {
            programId: programId,
            countryId: countryId,
          },
          queryString: {
            nominationIds: selectedProgramCountryNominationIds.join(','),
            exportType: exportedType,
          },
        },
        resourceType: ResourceTypes.ExportedProgramCountryNominations,
      });
    } else {
      await download({
        data: {
          routeData: {
            programId: programId,
            countryId: countryId,
          },
          queryString: {
            exportType: exportedType,
          },
        },
        resourceType: ResourceTypes.ExportedProgramCountryNominations,
      });
    }
    setShowExportNominationsModal(false);
  };

  const onExportNomination = async (exportedType: ExportType): Promise<void> => {
    if (selectedProgramCountryNominationId > 0) {
      await download({
        data: {
          routeData: {
            nominationId: selectedProgramCountryNominationId,
          },
          queryString: {
            exportType: exportedType,
          },
        },
        resourceType: ResourceTypes.ExportedNomination,
      });
    }
    setShowExportNominationModal(false);
    setSelectedProgramCountryNominationId(0);
  }

  const getCountryAwardStatistic = async (programId: number, countryId: number): Promise<void> => {
    try {
      const countryAwardStatistic = await apiStore.getCountryAwardStatistic(instance, programId, countryId);
      countryAwardPageUiStore.setCountryAwardStatistic(countryAwardStatistic);
    } catch (error) {
      // TODO - handle error
    }
  }

  const updateCountryAwardNominationScrubbingStatus = async (programId: number, countryId: number, nominationId: number, scrubbingStatus: NominationScrubbingStatus): Promise<void> => {
    try {
      await apiStore.updateCountryAwardNominationScrubbingStatus(instance, programId, countryId, nominationId, scrubbingStatus);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const onCancelScrubbing = () => {
    sortedCountryNominationSummaries.forEach(summary => summary.selected = false);
    setShowDisqualifyScrubbingModal(false);
    setShowPassScrubbingModal(false);
    setSelectedCountryNominationSummaries([]);
    setSelectedProgramCountryNominationIds([]);
  }

  const onPassNominations = async () => {
    setIsLoading(true);

    const updatePromises = selectedCountryNominationSummaries.map(nomination =>
      updateCountryAwardNominationScrubbingStatus(programId, countryId, nomination.nominationId, {
        ...blankNominationScrubbingStatus(),
        nominationId: nomination.nominationId,
        scrubbingStatus: 'Passed'
      })
    );

    await Promise.allSettled(updatePromises);

    await getCountryAwardStatistic(programId, countryId);
    await getCountryAwardNominationSummaries(programId, countryId, searchText);
    setIsLoading(false);
    onCancelScrubbing();
  };

  const onSortCountryNominationSummaryTable = (nominationSummaries: CountryNominationSummary[], key: NominationSummarySortKey) => {
    const isDescending = lastSortedKey === key;
    if (isDescending) {
      setLastSortedKey('');
    }
    else {
      setLastSortedKey(key);
    }
    setShowArrowOnKey(key);
    const sortedSummaries = sortCountryNominationSummariesByKey(nominationSummaries, key, isDescending);
    setSortedCountryNominationSummaries(sortedSummaries);
    onSetSortedCountryNominationSummaries(sortedSummaries);
  }

  const onLoadCountryNominationSummaries = (): void => {
    setSortedCountryNominationSummaries(countryNominationSummaries);
    onSetSortedCountryNominationSummaries(countryNominationSummaries);
  }

  const scrubbingStatusClassNameColumn = (status: string): string => {
    if (status === 'Incomplete') return 'text--orange';
    if (status === 'Passed') return 'text--green';
    if (status === 'Disqualified') return 'text--red';
    return '';
  }

  const handleSelectedCountryNominationSummary = async (nominationSummary: CountryNominationSummary): Promise<void> => {
    setIsLoading(true);
    countryAwardPageUiStore.setSelectedCountryNominationSummary(nominationSummary);
    try {
      const nominationRole = await apiStore.getCountryAwardNominationRole(
        instance,
        programId,
        countryId,
        nominationSummary.nominationId,
      );
      countryAwardPageUiStore.setSelectedNominationRole(nominationRole);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const canPass = areAllSelectedPassEligible(selectedCountryNominationSummaries);

  const renderPassButton = (): React.JSX.Element => {
    const passButton = (
      <Button
        appearance='secondary'
        disabledFocusable={!canPass}
        onClick={() => { if (canPass) setShowPassScrubbingModal(true); }}
      >
        Pass
      </Button>
    );

    if (canPass) return passButton;

    return (
      <Tooltip content={PASS_INELIGIBLE_TOOLTIP} relationship='description' withArrow>
        {passButton}
      </Tooltip>
    );
  }

  const getCompanyName = (nominationSummary: CountryNominationSummary) => {
    if (!awardStore.isWinnersWindowOpen) {
      return nominationSummary.company ?? '';
    }

    const firstQuestionnaire = nominationSummary.questionnaireSummaries?.[0];
    return firstQuestionnaire?.companyName || nominationSummary.company || '';
  };

  useEffect(() => {
    onLoadCountryNominationSummaries();
  }, [countryNominationSummaries]);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <div
      className='country-nomination-summary-table'
    >
      {
        isLoading && <Loading />
      }
      {
        isShowDisqualifyScrubbingModal &&
        <MultipleDisqualifyScrubbingModal
          onCancel={() => onCancelScrubbing()}
          onUpdateScrubbingStatus={async (nominationId, scrubbingStatus) => {
            await updateCountryAwardNominationScrubbingStatus(programId, countryId, nominationId, scrubbingStatus);
            await getCountryAwardStatistic(programId, countryId);
            await getCountryAwardNominationSummaries(programId, countryId, searchText);
          }}
          selectedNominationSummaries={selectedCountryNominationSummaries}
        />
      }
      {
        isShowPassScrubbingModal &&
        <PassScrubbingModal
          onCancelButtonClicked={() => onCancelScrubbing()}
          onSaveButtonClicked={() => onPassNominations()}
        />
      }
      {
        isShowExportNominationFieldsModal &&
        <ExportNominationFieldsModal
          isModalOpen={isShowExportNominationFieldsModal}
          isDownloading={isDownloadServiceInFlight}
          isCountryAward
          onCancelButtonClicked={() => onCancelExporting()}
          onExportButtonClicked={(selectedFields) => onExportNominations(selectedFields)}
        />
      }
      {
        isShowExportNominationsModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationsModal}
          isDownloading={isDownloadServiceInFlight}
          onCancelButtonClicked={() => setShowExportNominationsModal(false)}
          onExportButtonClicked={(exportedType) => onExportNominationsZip(exportedType)}
        />
      }
      {
        isShowExportNominationModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationModal}
          isDownloading={isDownloadServiceInFlight}
          onCancelButtonClicked={() => {
            setShowExportNominationModal(false);
            setSelectedProgramCountryNominationId(0);
          }}
          onExportButtonClicked={(exportedType) => onExportNomination(exportedType)}
        />
      }
      {showOverwriteConfirm && (
        <AiScrubOverwriteConfirmModal
          onCancel={() => setShowOverwriteConfirm(false)}
          onConfirm={() => { setShowOverwriteConfirm(false); setIsAiScrubbingDialogOpen(true); }}
        />
      )}
      <AiScrubbingTriggerDialog
        isOpen={isAiScrubbingDialogOpen}
        awardId={countryAwardId}
        programId={programId}
        countryId={countryId}
        defaultScrubbingRubric={defaultScrubbingRubric}
        scope='country-awards'
        onClose={(batchRunId, isEvaluation) => {
          setIsAiScrubbingDialogOpen(false);
          if (batchRunId) {
            countryAwardPageUiStore.setActiveBatchRunId(batchRunId);
            countryAwardPageUiStore.setIsEvaluationRun(isEvaluation);
          }
        }}
      />
      <DisqualificationReasoningDialog
        isOpen={!!reasoningDialogSummary}
        nominationSummary={reasoningDialogSummary}
        loadAssessments={(nominationId) => apiStore.getCountryAiAssessment(instance, programId, countryId, nominationId)}
        onOverride={() => {
          setOverrideDialogSummary(reasoningDialogSummary);
          setReasoningDialogSummary(undefined);
        }}
        onClose={() => setReasoningDialogSummary(undefined)}
      />
      <ScoringReasoningDialog
        isOpen={!!scoringDialogSummary}
        nominationSummary={scoringDialogSummary}
        loadAssessments={(nominationId) => apiStore.getCountryAiAssessment(instance, programId, countryId, nominationId)}
        loadJudgingQuestions={() => apiStore.getStaffJudgingQuestions(instance, countryAwardId)}
        loadHumanRatings={(nominationId) => apiStore.getHumanRatings(instance, countryAwardId, nominationId)}
        emptyStateLabel={noJudgingEmptyStateLabel}
        onClose={() => setScoringDialogSummary(undefined)}
      />
      {
        overrideDialogSummary &&
        <OverrideScrubbingModal
          nominationTitle={overrideDialogSummary.title}
          onCancel={() => setOverrideDialogSummary(undefined)}
          onOverride={async (justification) => {
            await apiStore.overrideCountryAwardNominationScrubbing(instance, programId, countryId, overrideDialogSummary.nominationId, justification);
            setOverrideDialogSummary(undefined);
            await getCountryAwardStatistic(programId, countryId);
            await getCountryAwardNominationSummaries(programId, countryId, searchText);
          }}
        />
      }
      <FlexBox
        wrap
        isFullWidth
        aligns='center'
        content='space-between'
        className='country-nomination-summary-table__header'
      >
        <InfoLabel
          info={{
            children: getTooltipContentByFieldName('staff-award-page', 'searchCountryNominationInput'),
            className: commonStyles.tooltip,
          }}
        >
          <SearchBox
            className={commonStyles.inputField}
            placeholder='Search'
            value={searchText}
            onChange={(_, data) => onSearchTextChanged(data.value)}
          />
        </InfoLabel>

        <FlexBox
          className='header__buttons'
        >
          <FlexBox
            as='button'
            className='button-as-link'
            aligns='center'
            onClick={() => setShowExportNominationFieldsModal(true)}
          >
            <ArrowDownload16Regular />
            Export to Excel
          </FlexBox>
          <FlexBox
            as='button'
            className='button-as-link'
            aligns='center'
            onClick={() => setShowExportNominationsModal(true)}
          >
            <ArrowDownload16Regular />
            Download all nominations
          </FlexBox>
          <Button
            appearance='secondary'
            disabled={selectedProgramCountryNominationIds.length === 0}
            onClick={() => setShowExportNominationsModal(true)}
          >
            Download
          </Button>
          {
            isScrubbingWindowOpen &&
            <>
              {canTriggerAiScrub && (
                <>
                  <Tooltip content={AI_SCRUB_OVERWRITE_WARNING} relationship='description' withArrow>
                    <Button
                      appearance='primary'
                      onClick={() => void onAiReviewClicked()}
                      disabled={(apiEndpointCallStatesStore.inFlightStatusMap.get('startCountryAiScrubBatch') ?? false) || countryAwardPageUiStore.isBatchInProgress}
                    >
                      AI Review
                    </Button>
                  </Tooltip>
                  <BatchProgressIndicator store={countryAwardPageUiStore} onCancel={onCancelBatch} inFlightKey='cancelCountryAiScrubBatch' />
                </>
              )}
              <Button
                appearance='secondary'
                disabled={!selectedCountryNominationSummaries.length}
                onClick={() => {
                  if (selectedCountryNominationSummaries.length > 0)
                    setShowDisqualifyScrubbingModal(true);
                }}
              >
                Disqualify
              </Button>
              {renderPassButton()}
            </>
          }
        </FlexBox>
      </FlexBox>

      {renderBatchCompletionSummary()}

      <div
        className='country-nomination-summary-table__table'
      >
        <Table>
          <TableHeader>
            <TableRow>
              {
                awardStore.isJudgingWindowOpen
                && someNominationsHaveAssignedJudge
                && <TableHeaderCell className='col-indicator' />
              }
              {
                someNominationsHaveWinnerRole
                && <TableHeaderCell className='col-indicator' />
              }
              <TableHeaderCell
                onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'title')}
                className='col-nomination'
              >
                Nomination
                {showArrow('title')}
              </TableHeaderCell>
              {
                awardStore.isNominationWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'status')}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'last-edited')}
                  >
                    Last Edited
                    {showArrow('last-edited')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'alias')}
                  >
                    Alias
                    {showArrow('alias')}
                  </TableHeaderCell>
                </>
              }
              {
                (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) &&
                <>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'company')}
                    className={awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}
                  >
                    Company
                    {showArrow('company')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'status')}
                    className={`col-status ${awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}`}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'score')}
                    className={awardStore.isWinnersWindowOpen ? 'autoSize-column' : ''}
                  >
                    Score
                    {showArrow('score')}
                  </TableHeaderCell>
                </>
              }
              {
                awardStore.isWinnersWindowOpen &&
                <>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'questionnaire-status')}
                  >
                    Questionnaire
                    {showArrow('questionnaire-status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'questionnaire-last-updated-date')}
                  >
                    Last Edited
                    {showArrow('questionnaire-last-updated-date')}
                  </TableHeaderCell>
                </>
              }
              {
                isScrubbingWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'scrubbing-status')}
                  >
                    Scrubbing Status
                    {showArrow('scrubbing-status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='reviewed-by-col'
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'scrubbing-by')}
                  >
                    Reviewed By
                    {showArrow('scrubbing-by')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className='assigned-since-col'
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'scrubbing-by-assigned-date')}
                  >
                    Assigned Since
                    {showArrow('scrubbing-by-assigned-date')}
                  </TableHeaderCell>
                </>
              }
              {
                isJudgingWindowOpen &&
                <>
                  <TableHeaderCell
                    className='col-status'
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'status')}
                  >
                    Status
                    {showArrow('status')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'assigned-to')}
                  >
                    Assigned to
                    {showArrow('assigned-to')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'assigned-date')}
                  >
                    Assigned since
                    {showArrow('assigned-date')}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSortCountryNominationSummaryTable(sortedCountryNominationSummaries, 'judged')}
                  >
                    Judged
                    {showArrow('judged')}
                  </TableHeaderCell>
                </>
              }
              <TableHeaderCell className='col-actions' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {
              sortedCountryNominationSummaries.map((nominationSummary) => {
                const isRuleDisqualified = (nominationSummary.scrubbingStatusId === ScrubbingStatusId.Disqualified
                  || nominationSummary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified'])
                  && nominationSummary.scrubbingSourceCode === 'Rule';
                const isAgentDisqualified = nominationSummary.scrubbingStatusId === ScrubbingStatusId['Pending-Disqualified']
                  && nominationSummary.scrubbingSourceCode === 'LLM';
                return (
                  <Fragment
                    key={nominationSummary.nominationId}
                  >
                    <TableRow>
                      {
                        (awardStore.isJudgingWindowOpen && someNominationsHaveAssignedJudge) &&
                        <TableCell className='col-indicator'>
                          <Button
                            appearance='transparent'
                            title='expand'
                            icon={expandedRows.includes(nominationSummary.nominationId) ? <ChevronDown12Regular /> : <ChevronUp12Regular />}
                            className='expanded-rows-icon'
                            onClick={() => handleToggleJudgeRows(nominationSummary.nominationId)}
                          />
                        </TableCell>
                      }
                      {
                        someNominationsHaveWinnerRole &&
                        <TableCell className={`col-indicator col-indicator--${nominationSummary.role?.toLowerCase()}`}>
                          <StarFilled
                            title={nominationSummary.role}
                            color={nominationSummary.role === 'Winner' ? '#FFB900' : '#BDD8E3'}
                          />
                        </TableCell>
                      }
                      <TableCell className='col-nomination'>
                        <NavLink
                          to={RoutePaths.Awards + '/' + countryAwardId + RoutePaths.Nominations + '/' + nominationSummary.nominationId + '/countries/' + countryId}
                          end
                        >
                          {nominationSummary.title}
                        </NavLink>
                      </TableCell>
                      {
                        awardStore.isNominationWindowOpen &&
                        <>
                          <TableCell
                            className={`col-status ${nominationSummary.statusDisplayName === 'Incomplete' ? 'text--orange' : ''}`}
                          >
                            {nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.lastEditedDate ? format(utcToZonedTime(parseISO(nominationSummary.lastEditedDate!), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.alias}
                          </TableCell>
                        </>
                      }
                      {
                        (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) &&
                        <>
                          <TableCell>{getCompanyName(nominationSummary)}</TableCell>
                          <TableCell
                            className={!nominationSummary.scrubbingStatusDisplayName && nominationSummary.statusDisplayName === 'Incomplete' ? 'text--orange' : ''}
                          >
                            {nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>{nominationSummary.score ?? 0}</TableCell>
                        </>
                      }
                      {
                        awardStore.isWinnersWindowOpen &&
                        <>
                          <TableCell className='col-status'>
                            {
                              nominationSummary.questionnaireSummaries && nominationSummary.questionnaireSummaries.length > 0 &&
                              nominationSummary.questionnaireSummaries[0].status
                            }
                          </TableCell>
                          <TableCell>
                            {
                              nominationSummary.questionnaireSummaries && nominationSummary.questionnaireSummaries.length > 0 &&
                              nominationSummary.questionnaireSummaries[0].lastUpdatedDate && format(utcToZonedTime(parseISO(nominationSummary.questionnaireSummaries[0].lastUpdatedDate), 'America/Los_Angeles'), 'MM/dd/yyyy')
                            }
                          </TableCell>
                        </>
                      }
                      {
                        isScrubbingWindowOpen &&
                        <>
                          <TableCell
                            className={`col-status ${scrubbingStatusClassNameColumn(nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : '')}`}
                          >
                            {nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : ''}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.scrubbedBy && nominationSummary.scrubbedBy}
                          </TableCell>
                          <TableCell>
                            {nominationSummary.scrubbedByAssignedDate && format(utcToZonedTime(parseISO(nominationSummary.scrubbedByAssignedDate), 'America/Los_Angeles'), 'MM/dd/yyyy')}
                          </TableCell>
                        </>
                      }
                      {
                        isJudgingWindowOpen &&
                        <>
                          <TableCell
                            className={`col-status ${scrubbingStatusClassNameColumn(nominationSummary.scrubbingStatusDisplayName || 'Incomplete')}`}
                          >
                            {nominationSummary.scrubbingStatusDisplayName ? nominationSummary.scrubbingStatusDisplayName : nominationSummary.statusDisplayName}
                          </TableCell>
                          <TableCell>
                            {
                              (nominationSummary.judges && nominationSummary.judges.length) > 0
                                ? getUserNames([nominationSummary.judges[0].user]) + (
                                  nominationSummary.judges.length > 1 && !expandedRows.includes(nominationSummary.nominationId)
                                    ? ` +${nominationSummary.judges.length - 1}`
                                    : ''
                                )
                                : ''
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (nominationSummary.judges && nominationSummary.judges.length) > 0 ? format(utcToZonedTime(parseISO(nominationSummary.judges[0].assignmentDate), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''
                            }
                          </TableCell>
                          <TableCell>{nominationSummary.numberOfCompletedJudgment}</TableCell>
                        </>
                      }
                      <TableCell
                        tabIndex={0}
                        className='col-actions'
                        {...focusableGroupAttr}
                      >
                        <FlexBox
                          content='end'
                        >
                          <Button
                            title='Download'
                            appearance='transparent'
                            icon={<ArrowDownloadRegular />}
                            onClick={() => {
                              setSelectedProgramCountryNominationId(nominationSummary.nominationId);
                              setShowExportNominationModal(true);
                            }}
                          />
                          {
                            canAssignWinners && (
                              <Radio
                                aria-label='select award nomination'
                                title='select award nomination'
                                id={`table-checked-${nominationSummary.nominationId}`}
                                checked={countryAwardPageUiStore.selectedCountryNominationSummary?.nominationId === nominationSummary.nominationId}
                                onChange={() => handleSelectedCountryNominationSummary(nominationSummary)}
                              />
                            )
                          }
                          <Checkbox
                            aria-label='select nomination'
                            title={
                              isRuleDisqualified
                                ? 'Disqualified by rule - cannot be passed'
                                : isAgentDisqualified
                                  ? 'AI recommends disqualification - review before passing'
                                  : 'select nomination'
                            }
                            disabled={isRuleDisqualified}
                            checked={!isRuleDisqualified && (nominationSummary.selected ?? false)}
                            onChange={(_, data) => onNominationCheckedChange(nominationSummary, data.checked as boolean)}
                          />

                        </FlexBox>
                      </TableCell>
                    </TableRow>
                    {
                      isJudgingWindowOpen &&
                      expandedRows.includes(nominationSummary.nominationId) &&
                      nominationSummary.judges
                        ?.filter(judge => judge.user)
                        .slice(1)
                        .map(judge => (
                          <TableRow key={judge.user.userId}>
                            <TableCell className='col-indicator' />
                            <TableCell />
                            <TableCell />
                            <TableCell>{getUserNames([judge.user])}</TableCell>
                            <TableCell>{format(utcToZonedTime(parseISO(judge.assignmentDate), 'America/Los_Angeles'), 'MM/dd/yyyy')}</TableCell>
                            <TableCell />
                            <TableCell />
                          </TableRow>
                        ))
                    }
                    {
                      awardStore.isWinnersWindowOpen && nominationSummary.questionnaireSummaries &&
                      nominationSummary.questionnaireSummaries
                        .filter(value => value.companyName !== nominationSummary.questionnaireSummaries[0].companyName)
                        .map(value => {
                          return (
                            <TableRow
                              key={value.nominationId}
                            >
                              <TableCell className='col-indicator' />
                              <TableCell />
                              <TableCell>{value.companyName}</TableCell>
                              <TableCell>{value.status}</TableCell>
                              <TableCell>{value.lastUpdatedDate ? format(utcToZonedTime(parseISO(value.lastUpdatedDate), 'America/Los_Angeles'), 'MM/dd/yyyy') : ''}</TableCell>
                            </TableRow>);
                        })
                    }
                  </Fragment>
                );
              })
            }
          </TableBody>
        </Table>
      </div>
      {renderScoringEmptyState()}
    </div>
  );
}

export const CountryNominationSummaryTable = observer(CountryNominationSummaryTableImpl);
